import React, { useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import injectStyles from 'react-jss'
import { withStyles } from '@material-ui/core/styles'
import CircularProgress from '@material-ui/core/CircularProgress'

import { pushApiError } from '../../../Reducers/Errors/actions'

import QuizQuestionItem from '../blocks/QuizQuestionItem/container/QuizQuestionItem'

import { setQuiz } from '../../../Module/graphQL'
import { useAmbassadorsRequest } from '../../../Containers/City/Page/Ambasadors/gql'
import {
  QUIZ_QUESTION_TYPES,
  QUIZ_TYPES,
  MULTIPLE_CHOICE_ANSWER_ERROR,
  DELETE_ALL_QUESTIONS_ERROR,
} from '../../../shared/constants'

import styles from '../styles'
import { checkIfShouldDisableSaveButton } from './util'
import SubmitButton from 'Components/Form/SubmitButton'
import ArrowUp from '../../../Assets/Images/icons/arrowUp'
import VideoQuizQuestionsFormView from '../component/VideoQuizQuestionsFormView'

const ColorCircularProgress = withStyles({
  root: {
    color: 'white',
  },
})(CircularProgress)

const titleToTypeMap = {
  [QUIZ_TYPES.VIDEO_ONBOARDING_PART_ONE]: 'VO Part 1',
  [QUIZ_TYPES.VIDEO_ONBOARDING_PART_TWO]: 'VO Part 2',
  [QUIZ_TYPES.VIDEO_ONBOARDING_PART_THREE]: 'VO Part 3',
}

const VideoQuizQuestionsForm = ({
  cityId,
  campaignId,
  fetchQuizQuestions,
  quizQuestions,
  videoOnboardingPart,
  numberOfQuestions,
  classes,
}) => {
  const createdQuestions = useRef(1)
  const createdOptions = useRef(1)
  const [uploading, setUploading] = useState(false)
  const [quizQuestionsList, setQuizQuestionsList] = useState([])
  const [quizQuestionsData, setQuizQuestionsData] = useState({})
  const [canSave, setCanSave] = useState(true)
  const [opened, setOpened] = useState(false)
  const dispatch = useDispatch()
  const { getAmbassadors } = useAmbassadorsRequest(cityId)

  const fetchAmbassadorsInTest = async () => {
    const { totalCount } = await getAmbassadors(
      {
        statuses: ['QUIZ_IN_PROGRESS'],
      },
      {
        isFresh: true,
      },
    )
    return totalCount
  }

  useEffect(() => {
    let formattedQuizQuestions = {}
    quizQuestions.forEach(quizQuestion => {
      const { id, typeChoice, question, quizAnswers } = quizQuestion
      const uniqueIds = []
      const correctAnswerIndexes = []
      const choices = quizAnswers.map(({ answer, isRight }, index) => {
        uniqueIds.push(createUniqueOptionId())
        if (isRight) correctAnswerIndexes.push(index)
        return answer
      })
      const description = question
      const type = typeChoice
      formattedQuizQuestions[id] = {
        id,
        choices,
        uniqueIds,
        correctAnswerIndexes,
        description,
        type,
      }
    })
    setQuizQuestionsData(formattedQuizQuestions)
  }, [quizQuestions])

  useEffect(() => {
    const quizQuestionsDataArr = Object.values(quizQuestionsData)

    const disableSaveButton = () => {
      setCanSave(checkIfShouldDisableSaveButton(quizQuestionsDataArr))
    }

    if (!quizQuestionsDataArr || !quizQuestionsDataArr.length) {
      return setQuizQuestionsList([])
    }

    const quizQuestionsList = quizQuestionsDataArr.map(quizQuestionData => {
      const {
        id,
        choices,
        uniqueIds,
        correctAnswerIndexes,
        description,
        type,
      } = quizQuestionData
      return (
        <QuizQuestionItem
          key={id}
          questionId={id}
          choices={choices}
          uniqueIds={uniqueIds}
          correctAnswerIndexes={correctAnswerIndexes}
          description={description}
          type={type}
          cityId={cityId}
          onAddOption={handleAddOption}
          onDeleteOption={handleDeleteOption}
          onDeleteQuestion={handleDeleteQuestion}
          onSetCorrectAnswer={handleSetCorrectAnswer}
          onOptionChange={handleOptionChange}
          onQuestionChange={handleQuestionChange}
          onQuestionTypeChange={handleQuestionTypeChange}
        />
      )
    })
    disableSaveButton()
    setQuizQuestionsList(quizQuestionsList)
  }, [quizQuestionsData])

  const createUniqueOptionId = () => {
    return createdOptions.current++
  }

  const isPossibleSubmit = () => {
    return Object.values(quizQuestionsData).reduce((acc, value) => {
      const { type, correctAnswerIndexes } = value
      if (
        type === QUIZ_QUESTION_TYPES.MULTIPLE_CHOICE &&
        correctAnswerIndexes.length < 2
      ) {
        acc = 1
      }
      return acc
    }, 0)
  }

  const submitQuestions = async () => {
    const canSubmit = isPossibleSubmit()
    if (canSubmit === 1) {
      dispatch(pushApiError(MULTIPLE_CHOICE_ANSWER_ERROR))
      return
    }

    if (
      numberOfQuestions &&
      Object.values(quizQuestionsData).length < numberOfQuestions
    ) {
      dispatch(
        pushApiError(
          `Please add minimum ${numberOfQuestions} questions to save the quiz`,
        ),
      )
      return
    }
    setUploading(true)
    const input = {
      type: videoOnboardingPart,
      cityId,
      quizQuestions: Object.values(quizQuestionsData).map(value => {
        const { id, choices, correctAnswerIndexes, description, type } = value
        return {
          id,
          typeChoice: type,
          question: description,
          quizAnswers: choices.map((choice, index) => ({
            answer: choice,
            isRight: correctAnswerIndexes.includes(index),
          })),
        }
      }),
    }
    await setQuiz(input)
    await fetchQuizQuestions()
    setUploading(false)
  }

  const handleAddOption = ({ questionId }) => {
    const quizData = JSON.parse(JSON.stringify(quizQuestionsData[questionId]))
    setQuizQuestionsData(quizQuestionsData => ({
      ...quizQuestionsData,
      [questionId]: {
        ...quizData,
        choices: [...quizData.choices, ''],
        uniqueIds: [...quizData.uniqueIds, createUniqueOptionId()],
      },
    }))
  }

  const handleOptionChange = ({ questionId, optionIndex, text }) => {
    const quizData = JSON.parse(JSON.stringify(quizQuestionsData[questionId]))
    setQuizQuestionsData(quizQuestionsData => {
      const choices = [...quizData.choices]
      choices[optionIndex] = text
      return {
        ...quizQuestionsData,
        [questionId]: {
          ...quizData,
          choices,
        },
      }
    })
  }

  const handleSetCorrectAnswer = ({ questionId, correctAnswerIndex }) => {
    const quizData = JSON.parse(JSON.stringify(quizQuestionsData[questionId]))
    if (quizData.correctAnswerIndexes.includes(correctAnswerIndex)) {
      return setQuizQuestionsData(quizQuestionsData => ({
        ...quizQuestionsData,
        [questionId]: {
          ...quizData,
          correctAnswerIndexes: quizData.correctAnswerIndexes.filter(
            correctIndex => correctIndex != correctAnswerIndex,
          ),
        },
      }))
    }

    setQuizQuestionsData(quizQuestionsData => ({
      ...quizQuestionsData,
      [questionId]: {
        ...quizData,
        correctAnswerIndexes:
          quizData.type == QUIZ_QUESTION_TYPES.MULTIPLE_CHOICE
            ? quizData.correctAnswerIndexes.concat(correctAnswerIndex).sort()
            : [correctAnswerIndex],
      },
    }))
  }

  const handleDeleteOption = ({ questionId, optionIndex }) => {
    const quizData = JSON.parse(JSON.stringify(quizQuestionsData[questionId]))
    const choices = [...quizData.choices]
    choices.splice(optionIndex, 1)
    const uniqueIds = [...quizData.uniqueIds]
    uniqueIds.splice(optionIndex, 1)
    const correctAnswerIndexes = [...quizData.correctAnswerIndexes].reduce(
      (acc, correctIndex) => {
        if (correctIndex != optionIndex) {
          acc.push(correctIndex > optionIndex ? correctIndex - 1 : correctIndex)
        }
        return acc
      },
      [],
    )
    setQuizQuestionsData(quizQuestionsData => ({
      ...quizQuestionsData,
      [questionId]: {
        ...quizData,
        choices,
        uniqueIds,
        correctAnswerIndexes,
      },
    }))
  }

  const handleDeleteQuestion = async ({ questionId }) => {
    if (cityId && Object.values(quizQuestionsData).length === 1) {
      const totalCount = await fetchAmbassadorsInTest()
      if (totalCount) {
        dispatch(pushApiError(DELETE_ALL_QUESTIONS_ERROR))
        return
      }
    }
    setQuizQuestionsData(quizQuestionsData => {
      const quizQuestionsDataProcessed = Object.keys(quizQuestionsData).reduce(
        (acc, id) => {
          if (id != questionId) {
            acc[id] = quizQuestionsData[id]
          }
          return acc
        },
        {},
      )
      return quizQuestionsDataProcessed
    })
  }

  const handleAddQuestion = () => {
    createdQuestions.current++
    setQuizQuestionsData(quizQuestionsData => ({
      ...quizQuestionsData,
      [`newQuestion-${createdQuestions.current}`]: {
        id: `newQuestion-${createdQuestions.current}`,
        uniqueIds: [createUniqueOptionId()],
        choices: [''],
        correctAnswerIndexes: [0],
        description: '',
        type: QUIZ_QUESTION_TYPES.SINGLE_CHOICE,
      },
    }))
  }

  const handleQuestionChange = ({ questionId, value }) => {
    const quizData = JSON.parse(JSON.stringify(quizQuestionsData[questionId]))
    setQuizQuestionsData(quizQuestionsData => ({
      ...quizQuestionsData,
      [questionId]: {
        ...quizData,
        description: value,
      },
    }))
  }

  const handleQuestionTypeChange = ({ questionId, newType }) => {
    const quizData = JSON.parse(JSON.stringify(quizQuestionsData[questionId]))
    setQuizQuestionsData(quizQuestionsData => ({
      ...quizQuestionsData,
      [questionId]: {
        ...quizData,
        type: newType,
        correctAnswerIndexes:
          newType == QUIZ_QUESTION_TYPES.SINGLE_CHOICE &&
          quizData.correctAnswerIndexes.length > 0
            ? [quizData.correctAnswerIndexes[0]]
            : quizData.correctAnswerIndexes,
      },
    }))
  }

  const onSectionPress = () => {
    setOpened(!opened)
  }
  return (
    <VideoQuizQuestionsFormView opened={opened}>
      <div className={classes.header} onClick={onSectionPress}>
        <div className={classes.title}>
          {titleToTypeMap[videoOnboardingPart]}
        </div>
        <div className={!opened ? classes.rotate180 : null}>
          <ArrowUp />
        </div>
      </div>
      <div hidden={!opened}>
        {quizQuestionsList}
        <footer className={classes.footer}>
          <SubmitButton
            variant="contained"
            onClick={handleAddQuestion}
            className={classes.button}
            value="+ Add question"
          />
          <SubmitButton
            variant="contained"
            onClick={submitQuestions}
            className={classes.button}
            disabled={canSave}
            value={uploading ? <ColorCircularProgress size={25} /> : 'Save'}
          />
        </footer>
      </div>
    </VideoQuizQuestionsFormView>
  )
}

export default injectStyles(styles)(VideoQuizQuestionsForm)
